
import { Vue, Options } from "vue-class-component";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import {
  RtcData,
  BookingPayloadData,
  FlightRequest
} from "@/domain/entities/CargoV2";
import CargoV2FilterRtc from "@/app/ui/views/cargo-v2/components/cargo-v2-filter-rtc.vue";
import CargoV2CardRtc from "@/app/ui/views/cargo-v2/components/cargo-v2-card-rtc.vue";
import ModalPlaneAutoV3 from "@/app/ui/views/cargo-v2/components/modal-plane-auto-v3.vue";
import PaginationV3 from "@/app/ui/components/pagination-v3/index.vue";
import Skeleton from "@/app/ui/components/skeleton/index.vue";
import Loading from "@/app/ui/components/loading/index.vue";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { formatDateNormal, parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import { ModalMessageEntities, OptionsClass, Snackbar } from "@/domain/entities/MainApp";
import {
  CalculateVolumeApiRequestV2,
  CalculateVolumeRequestV2,
  RequestRtcList
} from "@/data/payload/api/CargoV2ApiRequest";
import { CheckEligible, useCargoV2 } from "../composables";
import { getHubData } from "@/app/infrastructures/misc/Cookies";
import { CreateCargoV3ApiRequest } from "@/data/payload/api/CargoV2ApiRequest";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { LocationController } from "@/app/ui/controllers/LocationController";
import {
  CALCULATE_TYPE,
  getConfigDimensionDivider,
  getConfigIsminimumVolumePerPiece,
  getConfigMinimumVolumePerPiece
} from "../modules/cargo-module";
import ButtonLoadMore from "../components/rtc-list/button-load-more.vue";

const { onCheckEligible } = useCargoV2();

@Options({
  components: {
    CargoV2FilterRtc,
    CargoV2CardRtc,
    PaginationV3,
    ModalPlaneAutoV3,
    Skeleton,
    Loading,
    ButtonLoadMore
  }
})
class TabRtcPlane extends Vue {
  rtcListLimit = 5;
  get rtcListData() {
    return this.rtcList.data.slice(0, this.rtcListLimit);
  }
  filterForm = {
    destination: [] as string[],
    client: [] as string[],
    hasMinimumWeight: false,
    hasMinimumPieces: false,
    sort: ""
  };

  showModalForm = false;
  get dataReadyToCargo() {
    return CargoV2Controller.rtcDetail;
  }
  isProcessFlow = false;
  bookingLoading = false;

  get form() {
    return CargoV2Controller.form;
  }

  get rtcDetail() {
    return CargoV2Controller.rtcDetail;
  }

  get isFirstLandingRtc() {
    return CargoV2Controller.isFirstLandingRtc;
  }

  async mounted() {
    await this.fetchReadyToCargoList();

    if (
      !this.rtcList.data.length &&
      !this.hasActiveFilter &&
      this.isFirstLandingRtc
    ) {
      this.$router.push("truck");
    }
  }

  get rtcList() {
    return CargoV2Controller.rtcList;
  }

  get isLoading() {
    return CargoV2Controller.rtcLoading;
  }

  get hasActiveFilter(): boolean {
    return (
      this.filterForm.destination.length > 0 ||
      this.filterForm.client.length > 0 ||
      this.filterForm.hasMinimumWeight ||
      this.filterForm.hasMinimumPieces ||
      !!this.filterForm.sort
    );
  }

  get errorContainer() {
    return {
      errorIcon: "ill_spot_search",
      errorTitle: this.$t("cargoV2.create.rtcList.notFound.title"),
      errorMessage: this.$t("cargoV2.create.rtcList.notFound.message")
    };
  }

  async fetchReadyToCargoList() {
    const destinationCode = this.filterForm.destination.some(
      (item) => item === ""
    )
      ? ""
      : this.filterForm.destination.join(",");

    const sortOptions: {
      [key: string]: { sortBy: string; orderBy: string };
    } = {
      terdekat: { sortBy: "ct_time", orderBy: "ASC" },
      terlama: { sortBy: "ct_time", orderBy: "DESC" },
      default: { sortBy: "rtc_id", orderBy: "ASC" }
    };

    const selectedSortOption =
      sortOptions[this.filterForm.sort] || sortOptions.default;

    const sortBy = selectedSortOption.sortBy;
    const orderBy = selectedSortOption.orderBy;

    await CargoV2Controller.fetchReadyToCargoList(
      new RequestRtcList({
        page: this.rtcList.pagination.page,
        limit: this.rtcList.pagination.limit,
        destinationCode: destinationCode,
        transportType: "plane",
        sortBy: sortBy,
        orderBy: orderBy,
        hasMinimumWeight: this.filterForm.hasMinimumWeight,
        hasMinimumKoli: this.filterForm.hasMinimumPieces,
        cotFilter: this.filterForm.sort === "terdekat" ? "soonest" : "oldest",
        clientParentIds: this.filterForm.client.length
          ? this.filterForm.client.map((item) => Number(item))
          : [],
        hubId: getHubData()?.hubId || 0
      })
    );
  }

  onFilter(form: any) {
    this.filterForm = form;
    this.rtcList.pagination.page = 1;
    this.fetchReadyToCargoList();
  }

  onChangePagination() {
    this.rtcList.pagination.page += 1;
    this.fetchReadyToCargoList();
  }

  async onProcess(data: RtcData) {
    this.handleProcess(data, true);
  }

  async onChooseSchedule(data: RtcData) {
    this.handleProcess(data, false);
  }

  async handleProcess(data: RtcData, isProcessFlow: boolean) {
    this.isProcessFlow = isProcessFlow;

    this.onBeforeProcess(data, "process", () => (this.showModalForm = true));
  }

  handleProductInactive() {
    const productFound = CargoV2Controller.cargoProductList.find(
      (product) => product.cargoProductTypeCode === this.form.productCargo
    );

    if (!productFound)
      this.form.productCargo =
        CargoV2Controller.cargoProductList[0].cargoProductTypeCode;
  }

  onReset() {
    this.showModalForm = false;
  }

  getFlightRequestFromRtc() {
    return CargoV2Controller.flightsData;
  }

  async onCalculateVolume() {
    const divider = getConfigDimensionDivider("plane");
    const isMinimumVolumePerPiece = getConfigIsminimumVolumePerPiece(
      "plane",
      "auto"
    );
    const minVolumePerPiece = getConfigMinimumVolumePerPiece("plane", "auto");
    return await CargoV2Controller.calculateVolume(
      new CalculateVolumeApiRequestV2({
        calculateType: CALCULATE_TYPE.INIT,
        calculateRequest: new CalculateVolumeRequestV2({
          cargoType: "plane",
          totalPieces: this.rtcDetail.totalPieces,
          length: 90,
          width: 60,
          currentGrossWeight: this.rtcDetail.totalWeight,
          volumeWeightReweigh: this.form
            .totalAccumulationEstimationVolumeWeightDataTable,
          chargeableWeightReweigh: this.form.cargoTotalEstimateChargeableWeight,
          divider: divider,
          isMinimumVolumePerPiece: isMinimumVolumePerPiece,
          minVolumePerPiece: minVolumePerPiece
        })
      })
    );
  }

  async onBooking() {
    this.bookingLoading = true;
    this.showModalForm = false;
    MainAppController.closeMessageModal();
    MainAppController.closeErrorMessage();

    const totalStt = this.form.bagOrStt.reduce((prev: number, curr: any) => {
      if (curr?.sttDetails) return prev + curr.sttDetails.length;
      return prev + 1;
    }, 0);

    try {
      await this.onCalculateVolume();
      const rtcDetail = this.rtcDetail;
      const flightRequest = this.getFlightRequestFromRtc();

      const res = await CargoV2Controller.createBooking(
        new CreateCargoV3ApiRequest({
          ...this.form,
          originAirportCode:
            flightRequest[0].flightOrigin,
          destinationAirportCode:
            flightRequest[flightRequest.length - 1].flightDestination ||
            LocationController.districtDetail.cityCode,
          estTotalCargoGrossweigth: rtcDetail.totalWeight,
          estTotalCargoVolumeweight: this.form
            .totalAccumulationEstimationVolumeWeightDataTable,
          partnerName:
            AccountController.accountData.account_type_detail.company_name,
          partnerType: AccountController.accountData.normalizedTypeUser,
          actualTotalCargoGrossweigth: this.form.actualTotalCargoGrossweigth,
          actualTotalCargoVolumeweight: this.form.actualTotalCargoVolumeweight,
          totalStt: totalStt,
          totalPiece: rtcDetail.totalPieces,
          cargoType: new OptionsClass({ value: "plane" }),
          originCityCode: rtcDetail.originCode,
          destinationCityCode: new OptionsClass({
            code: rtcDetail.destinationCode,
            name: rtcDetail.destinationName
          }),
          bagOrStt: this.form.bagOrStt,
          cargoActualPiece: rtcDetail.totalPieces,
          rtcId: rtcDetail.id,
          isFromRtc: true,
          bookingPayload: new BookingPayloadData({
            awbNo: "",
            shcCode: this.form.shc,
            flightRequest
          }),
          departureDate: CargoV2Controller.departureDate,
          arrivalDate: CargoV2Controller.arrivalDate,
          cargoOverCutoffLimit: CargoV2Controller.cargoOverCutoffLimit,
        })
      );

      this.onCreateResponse(res);
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          AccountController.accountData.accountIsForeign
            ? this.$t("cargoV2.create.error.failedToCargoBooking")
            : this.$t("cargoV2.create.error.creationFailed"),

          this.onBooking,
          this.onRefresh
        )
      );
    } finally {
      this.bookingLoading = false;
    }
  }

  async onSubmitChooseSchedule() {
    if (!this.isProcessFlow) {
      this.showModalForm = false;
      this.$router.push({
        path: "/cargo-v2/create/search-flight",
        query: { rtcId: `${this.dataReadyToCargo.id}` }
      });
      return;
    }
    await this.onBooking();
  }

  onCreateResponse(resp: any) {
    if (resp.bookingRequestId && !resp.isCargoUsingAwb) {
      const popupIllustration = "on-process-booking-cargo";
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          title: this.$t("cargoV2.create.modal.bookingProcessing.title", {
            bookingRequestId: resp.bookingRequestId
          }),
          message: this.$t("cargoV2.create.modal.bookingProcessing.message"),
          textSuccess: this.$t("cargoV2.create.common.okUpper"),
          image: popupIllustration,
          onSubmit: () => {
            MainAppController.closeMessageModal();
            this.fetchReadyToCargoList();
          },
        })
      );
      return;
    }
    const message = this.$t("cargoV2.create.snackbar.booked", {
      cargoNo: resp.cargoNo,
      destination: this.dataReadyToCargo.destinationCode
    });
    MainAppController.setSnackbarData(
      new Snackbar({
        type: "success",
        message: message
      })
    );
    MainAppController.setAutoClose(true);
    MainAppController.setSnackbar(true);
    this.fetchReadyToCargoList();
  }

  onErrorCreateCargo(err: any, onRetry: () => void) {
    MainAppController.showErrorMessage(
      parsingErrorResponse(err, this.$t("cargoV2.create.error.bookingFailedTitle"), () => {
        MainAppController.closeErrorMessage();
        onRetry();
      })
    );
  }

  async onEdit(data: RtcData) {
    this.onBeforeProcess(data, "rtc-edit", () =>
      this.$router.push(`/cargo-v2/create/rtc/${data.id}`)
    );
  }

  async onBeforeProcess(data: RtcData, flow: string, success: () => void) {
    try {
      await CargoV2Controller.getDetailReadyToCargo({
        id: data.id,
        flow
      });

      await CargoV2Controller.mapRtcDetailToCargoForm();

      const cargoProductList = await CargoV2Controller.getCargoProductList(
        "active"
      );
      CargoV2Controller.setCargoProductList(cargoProductList);
      this.handleProductInactive();

      await onCheckEligible(
        new CheckEligible({
          success,
          close: () => {
            if (!this.form.bagOrStt.length) {
              this.fetchReadyToCargoList();
              return;
            }
            this.form.actualTotalCargoVolumeweight = this.form.totalAccumulationEstimationVolumeWeightDataTable;
            this.form.cargoActualPiece = this.form.totalAccumulationActualPieceDataTable;
            this.form.actualTotalCargoGrossweigth = this.form.totalAccumulationEstimationGrossWeightDataTable;
            CargoV2Controller.setForm(this.form);
            this.$router.push(`/cargo-v2/create/rtc/${data.id}`);
          }
        })
      );
    } catch (error) {
      this.fetchReadyToCargoList();
    }
  }

  onRefresh() {
    CargoV2Controller.resetForm();
    this.fetchReadyToCargoList();
  }
}

export default TabRtcPlane;
